import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  getDemoStatus,
  type DemoLoginHint,
  type DemoStatus,
} from '@/services/demo';

const NORMAL_STATUS: DemoStatus = {
  demo: false,
  read_only: false,
};
const DEMO_BUILD = import.meta.env.MODE === 'demo';

export function createDemoFallbackStatus(
  demoBuild = DEMO_BUILD,
): DemoStatus {
  return demoBuild
    ? { demo: true, read_only: true }
    : { ...NORMAL_STATUS };
}

function normalizeLoginHint(value: unknown): DemoLoginHint | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const hint = value as Record<string, unknown>;
  if (typeof hint.username !== 'string' || typeof hint.password !== 'string') {
    return undefined;
  }
  const username = hint.username.trim();
  const password = hint.password.trim();
  return username && password ? { username, password } : undefined;
}

export function normalizeDemoStatus(
  value: unknown,
  demoBuild = DEMO_BUILD,
): DemoStatus {
  if (!value || typeof value !== 'object') {
    return createDemoFallbackStatus(demoBuild);
  }
  const status = value as Record<string, unknown>;
  if (demoBuild) {
    return {
      demo: true,
      read_only: true,
      login_hint:
        status.demo === true
          ? normalizeLoginHint(status.login_hint)
          : undefined,
    };
  }
  const demo = status.demo === true;
  if (!demo) return { ...NORMAL_STATUS };

  return {
    demo: true,
    read_only: status.read_only === true,
    login_hint: normalizeLoginHint(status.login_hint),
  };
}

export const useDemoStore = defineStore('demo', () => {
  const status = ref<DemoStatus>(createDemoFallbackStatus());
  const loaded = ref(false);
  const loading = ref(false);
  let pending: Promise<DemoStatus> | undefined;

  const isDemo = computed(() => status.value.demo);
  const isReadOnly = computed(
    () => status.value.demo && status.value.read_only,
  );
  const loginHint = computed(() =>
    status.value.demo ? status.value.login_hint : undefined,
  );

  function load(force = false): Promise<DemoStatus> {
    if (!DEMO_BUILD) {
      loaded.value = true;
      return Promise.resolve(status.value);
    }
    if (pending) return pending;
    if (loaded.value && !force) return Promise.resolve(status.value);

    loading.value = true;
    pending = getDemoStatus()
      .then((response) => {
        status.value = normalizeDemoStatus(response.data);
        return status.value;
      })
      .catch(() => {
        status.value = createDemoFallbackStatus();
        return status.value;
      })
      .finally(() => {
        loaded.value = true;
        loading.value = false;
        pending = undefined;
      });
    return pending;
  }

  return {
    status,
    loaded,
    loading,
    isDemo,
    isReadOnly,
    loginHint,
    load,
  };
});
